import { Comparator, moment, Period, Utils } from "revlock-webutils";
import { updateRatablePlan } from "./plan/ApportionedRatablePlan";
import { Codes } from "./ErrorCode";
import RecognitionRule from "./reference/RecognitionRule";
import * as ArrangementUtils from "./ArrangementUtils";
import shortId from "shortid";
import cloneDeep from "lodash.clonedeep";
import ItemType from "./reference/ItemType";
import ModificationType from "./reference/ModificationType";
import {
    matchFieldsFromOrgConfig,
    isPsdMatchByProductOrFields
} from "./ServiceDeliveryMatchConfig";
import { matchLogToLine } from "./ServiceDeliveryLineMatcher";

function isTrueUpVariableConsiderationLog(log) {
    if (!log.usageType) {
        return false;
    }
    const usageType = String(log.usageType).trim().toLowerCase();
    if (usageType !== "true_up") {
        return false;
    }
    return log.variableQuantity != null || log.variableAmount != null;
}

export function calculateVariableConsideration(deliveryLogs) {
    let totalVariableQuantity = 0;
    let totalVariableAmount = 0;

    if (!deliveryLogs || !Array.isArray(deliveryLogs)) {
        return { totalVariableQuantity, totalVariableAmount };
    }

    deliveryLogs.forEach((log) => {
        if (isTrueUpVariableConsiderationLog(log)) {
            if (log.variableQuantity != null && !isNaN(log.variableQuantity)) {
                totalVariableQuantity += Number(log.variableQuantity);
            }
            if (log.variableAmount != null && !isNaN(log.variableAmount)) {
                totalVariableAmount += Number(log.variableAmount);
            }
        }
    });

    return { totalVariableQuantity, totalVariableAmount };
}

function filterOutTrueUpLogs(deliveryLogs) {
    if (!deliveryLogs || !Array.isArray(deliveryLogs)) {
        return deliveryLogs;
    }
    return deliveryLogs.filter((log) => !isTrueUpVariableConsiderationLog(log));
}

function normalizeVariableAdjMode(raw) {
    if (raw == null || String(raw).trim() === "") {
        return null;
    }
    const s = String(raw).trim().toLowerCase();
    if (s === "override") {
        return "Override";
    }
    return "Append";
}

function variableAdjModeForCatchDown(log, organization, orgConfig) {
    const explicit = normalizeVariableAdjMode(log.variableAdjMode);
    if (explicit) {
        return explicit;
    }
    const modeCfg = orgConfig?.find(
        (c) => c.id === "properties/variable_adjustment_mode"
    );
    const orgDefault = normalizeVariableAdjMode(modeCfg?.value) || "Append";

    return orgDefault;
}

/**
 * Resolves a true-up log's variable quantity and amount as numbers.
 *
 * A TRUE_UP log carries a quantity, an amount, or both. Variable_Quantity and
 * Variable_Amount are DECIMAL staging columns that cannot hold NULL, so a value
 * the source never sent arrives as 0. When one field is populated, a 0 in the
 * other means "not supplied" and must not be read as an instruction to reset
 * that side to zero. Both being 0 is an explicit full reversal of the line.
 */
function resolveTrueUpVariableValues(log) {
    const vq = log.variableQuantity;
    const va = log.variableAmount;
    let vqNum = vq != null && !isNaN(vq) ? Number(vq) : null;
    let vaNum = va != null && !isNaN(va) ? Number(va) : null;

    if (vqNum === 0 && vaNum != null && vaNum !== 0) {
        vqNum = null;
    }

    if (vaNum === 0 && vqNum != null && vqNum !== 0) {
        vaNum = null;
    }

    return { vqNum, vaNum };
}

function sortTrueUpVariableConsiderationLogs(logs) {
    return (logs || [])
        .slice()
        .sort(
            Comparator.getComparator(
                [
                    (l) => l.effectivePeriod || l.period || "",
                    (l) => l.logDate || "",
                    (l) => l.timestamp || 0
                ],
                [
                    Comparator.forType("period"),
                    Comparator.forType("date"),
                    Comparator.forType("number")
                ]
            )
        );
}

export function foldTrueUpQuantityAndExtended(
    baseQuantity,
    baseExtendedSalePrice,
    trueUpLogs,
    organization,
    orgConfig
) {
    const baseQty = Number(baseQuantity) || 0;
    const baseExt = Number(baseExtendedSalePrice) || 0;
    const logs = sortTrueUpVariableConsiderationLogs(
        (trueUpLogs || []).filter(isTrueUpVariableConsiderationLog)
    );
    let qty = baseQty;
    let ext = baseExt;

    logs.forEach((log) => {
        const mode = variableAdjModeForCatchDown(log, organization, orgConfig);
        const { vqNum, vaNum } = resolveTrueUpVariableValues(log);

        if (mode === "Override") {
            if (vqNum != null) {
                qty = vqNum;
            }
            if (vaNum != null) {
                ext = vaNum;
            }
        } else {
            if (vqNum != null) {
                qty += vqNum;
            }
            if (vaNum != null) {
                ext += vaNum;
            }
        }
    });

    return {
        quantity: qty,
        extendedSalePrice: ext,
        deltaVariableQuantity: qty - baseQty,
        deltaVariableAmount: ext - baseExt
    };
}

export function effectivePeriodFromDate(effDate, calendarConfig) {
    if (!effDate) {
        return null;
    }
    try {
        return Period.toActgPeriod(effDate, calendarConfig).period;
    } catch (e) {
        return null;
    }
}

export function trueUpFilterAnchorPeriod(revenueArrangement, calendarConfig) {
    if (!revenueArrangement) {
        return null;
    }
    if (revenueArrangement.trueUpAnchorDate) {
        return effectivePeriodFromDate(
            revenueArrangement.trueUpAnchorDate,
            calendarConfig
        );
    }
    return revenueArrangement.trueUpAnchorPeriod || null;
}

function trueUpEffectivePeriod(log, calendarConfig) {
    return (
        log.effectivePeriod ||
        log.period ||
        effectivePeriodFromDate(log.logDate, calendarConfig)
    );
}

export function splitTrueUpLogsByEffectivePeriod(
    trueUpLogs,
    anchorPeriod,
    calendarConfig
) {
    const oldLogs = [];
    const futureLogs = [];
    (trueUpLogs || []).forEach((log) => {
        if (!isTrueUpVariableConsiderationLog(log)) {
            return;
        }
        const logPeriod = trueUpEffectivePeriod(log, calendarConfig);
        if (
            anchorPeriod &&
            logPeriod &&
            Period.isBefore(logPeriod, anchorPeriod)
        ) {
            oldLogs.push(log);
        } else {
            futureLogs.push(log);
        }
    });
    return { oldLogs, futureLogs };
}

/**
 * True when variableQuantity was explicitly provided as a numeric value
 * (including 0). Missing / null / "" / whitespace do not count.
 */
function hasExplicitVariableQuantity(variableQuantity) {
    if (variableQuantity == null) {
        return false;
    }
    if (
        typeof variableQuantity === "string" &&
        variableQuantity.trim() === ""
    ) {
        return false;
    }
    return !isNaN(Number(variableQuantity));
}

/**
 * Applies true-up variableQuantity into rollforward incrementalPurchases.
 * Override: period gets (absoluteQty - currentCumulative). Append: period gets variableQuantity.
 * Explicit 0 overrides under Override; null/"" do not overwrite.
 * Mutates and returns incrementalPurchases.
 */
export function applyTrueUpQuantityToIncrementalPurchases(
    incrementalPurchases,
    deliveryLogs,
    organization,
    orgConfig,
    calendarConfig
) {
    const purchases = incrementalPurchases || {};
    const trueUpLogs = sortTrueUpVariableConsiderationLogs(
        (deliveryLogs || []).filter(isTrueUpVariableConsiderationLog)
    );
    if (trueUpLogs.length === 0) {
        return purchases;
    }

    let cumulative = Object.keys(purchases).reduce(
        (sum, period) => sum + (Number(purchases[period]) || 0),
        0
    );

    for (const log of trueUpLogs) {
        if (!hasExplicitVariableQuantity(log.variableQuantity)) {
            continue;
        }
        const variableQty = Number(log.variableQuantity);

        const logPeriod =
            log.effectivePeriod ||
            log.period ||
            Period.toPeriod(log.logDate, calendarConfig);
        const mode = variableAdjModeForCatchDown(log, organization, orgConfig);
        let delta;
        if (mode === "Override") {
            delta = variableQty - cumulative;
            cumulative = variableQty;
        } else {
            delta = variableQty;
            cumulative += variableQty;
        }
        if (delta === 0) {
            continue;
        }
        if (!purchases[logPeriod]) {
            purchases[logPeriod] = 0;
        }
        purchases[logPeriod] += delta;
    }

    return purchases;
}

/**
 * Restates plan dollars after variable-consideration true-ups.
 *
 * @param {Array<Object>} newRevenuePlan — plan rows with actgPeriod / planAmount / etc.
 * @param {Object} organization — for currentPeriod / isRestatementMode
 * @param {Array<Object>} deliveryLog — PSD rows
 * @param {Array<Object>} [trueUpLogs] — variable-consideration true_up logs
 * @param {number} [oldQuantity] — quantity before true-up mutation
 * @param {number} oldProductRevenue — allocated revenue before true-up mutation
 * @param {boolean} [isPercentDriver=false] — if true, use logPercentComplete and booking timeline (cumQty=1);
 *   if false, use unitsConsumed/unitsDelivered × blended $/unit timeline (oldQuantity required)
 * @param {boolean} [isRatable=false] — ratable path: selects the residual sink and enables the
 *   per-element split when the plan rows carry unitShare
 * @param {Object} [ratableDriverByPeriod] — period-level driver, used only when isRatable and no
 *   plan row carries unitShare
 */
export function applyTrueUpCatchDown({
    newRevenuePlan,
    organization,
    deliveryLog,
    trueUpLogs,
    oldQuantity,
    oldProductRevenue,
    isPercentDriver = false,
    isRatable = false,
    ratableDriverByPeriod,
    orgConfig
}) {
    if (!isPercentDriver && !oldQuantity) return;
    if (
        isPercentDriver &&
        (oldProductRevenue == null || !isFinite(oldProductRevenue))
    ) {
        return;
    }

    const sortedTrueUps = sortTrueUpVariableConsiderationLogs(trueUpLogs);

    let cumQty = isPercentDriver ? 1 : oldQuantity;
    let cumRev = oldProductRevenue;
    let priorRate = cumQty ? cumRev / cumQty : 0;
    const timeline = [
        { fromPeriod: "000000", rate: priorRate, isEvent: false }
    ];
    sortedTrueUps.forEach((log) => {
        const mode = variableAdjModeForCatchDown(log, organization, orgConfig);
        const { vqNum, vaNum } = resolveTrueUpVariableValues(log);
        const varQty = vqNum || 0;
        const varAmt = vaNum || 0;

        if (isPercentDriver) {
            if (mode === "Override" && vaNum != null) {
                cumRev = vaNum;
            } else {
                cumRev += varAmt;
            }
        } else if (mode === "Override") {
            let nextRev = cumRev;
            let nextQty = cumQty;
            if (vaNum != null) {
                nextRev = vaNum;
            }
            if (vqNum != null) {
                nextQty = vqNum;
            }
            cumRev = nextRev;
            cumQty = nextQty;
        } else {
            cumRev += varAmt;
            cumQty += varQty;
        }

        const newRate = cumQty ? cumRev / cumQty : 0;
        const eventPeriod = log.effectivePeriod || log.period;
        if (eventPeriod) {
            timeline.push({
                fromPeriod: eventPeriod,
                rate: newRate,
                isEvent: true,
                priorRate: priorRate
            });
        }
        priorRate = newRate;
    });

    const rateForPeriod = (period) => {
        let rate = timeline[0].rate;
        for (const entry of timeline) {
            if (entry.fromPeriod <= period) rate = entry.rate;
        }
        return rate;
    };

    const currentPeriod = organization && organization.currentPeriod;
    const isClosedPeriod = (p) =>
        currentPeriod ? Period.isBefore(p, currentPeriod) : false;

    const entriesByPeriod = {};
    newRevenuePlan.forEach((p) => {
        const period = p.actgPeriod?.period || p.period;
        if (!period) return;
        if (!entriesByPeriod[period]) entriesByPeriod[period] = [];
        entriesByPeriod[period].push(p);
        // Mark that this plan element was affected by true-up catch-down
        p.isTrueUpCatchDown = true;
    });

    // updatePSPlanRatable stamps unitShare on every element it builds, so the
    // period driver is the sum of the weights it allocates across — the two can
    // never disagree. Any other shape falls through to the driver map or the
    // delivery log, which keeps index-0 concentration.
    const hasUnitShare =
        isRatable && newRevenuePlan.some((p) => p.unitShare != null);

    const driverByPeriod = {};
    if (hasUnitShare) {
        Object.keys(entriesByPeriod).forEach((period) => {
            const driver = entriesByPeriod[period].reduce(
                (sum, p) => sum + Number(p.unitShare || 0),
                0
            );
            if (driver) {
                driverByPeriod[period] = driver;
            }
        });
    } else if (isRatable && ratableDriverByPeriod) {
        Object.keys(ratableDriverByPeriod).forEach((period) => {
            const driver = Number(ratableDriverByPeriod[period] || 0);
            if (driver) {
                driverByPeriod[period] = (driverByPeriod[period] || 0) + driver;
            }
        });
    } else {
        deliveryLog.forEach((log) => {
            const period = log.effectivePeriod || log.period;
            if (!period) return;
            const driver = isPercentDriver
                ? Number(log.logPercentComplete || 0)
                : Number(log.unitsConsumed || log.unitsDelivered || 0);
            if (driver) {
                driverByPeriod[period] = (driverByPeriod[period] || 0) + driver;
            }
        });
    }

    Object.keys(entriesByPeriod).forEach((period) => {
        const entries = entriesByPeriod[period];
        const driver = driverByPeriod[period] || 0;

        const rate = rateForPeriod(period);
        const target = driver * rate;

        if (!hasUnitShare) {
            entries.forEach((e, i) => {
                e.planAmount = i === 0 ? target : 0;
            });
            return;
        }

        const weightTotal = entries.reduce(
            (sum, e) => sum + Number(e.unitShare || 0),
            0
        );
        if (!weightTotal) {
            // weightTotal 0 implies driver 0 implies target 0, so this produces
            // what the allocation below would; it keeps the division defined.
            entries.forEach((e) => {
                e.planAmount = 0;
            });
            return;
        }

        // The running remainder goes to the last weighted element, not the last
        // element in the bucket: a zero-weight element (term-end leftover,
        // gap fill) must stay exactly zero rather than absorb float dust.
        const weightedCount = entries.filter(
            (e) => Number(e.unitShare || 0) !== 0
        ).length;
        let remaining = target;
        let seen = 0;
        entries.forEach((e) => {
            const weight = Number(e.unitShare || 0);
            if (!weight) {
                e.planAmount = 0;
                return;
            }
            seen += 1;
            const allocated =
                seen === weightedCount
                    ? remaining
                    : target * (weight / weightTotal);
            e.planAmount = allocated;
            remaining -= allocated;
        });
    });

    timeline.forEach((entry) => {
        if (!entry.isEvent) return;
        const eventPeriod = entry.fromPeriod;
        const priorClosedDriver = Object.keys(driverByPeriod).reduce(
            (sum, p) => {
                if (p >= eventPeriod) return sum;
                if (!isClosedPeriod(p)) return sum;
                return sum + driverByPeriod[p];
            },
            0
        );
        const catchUp = priorClosedDriver * (entry.rate - entry.priorRate);
        if (catchUp === 0) return;
        const targetEntries = entriesByPeriod[eventPeriod];
        if (targetEntries && targetEntries.length > 0) {
            targetEntries[0].planAmount =
                (targetEntries[0].planAmount || 0) + catchUp;
        }
    });

    const newProductRevenue = cumRev;
    const planTotal = newRevenuePlan.reduce(
        (s, p) => s + (p.planAmount || 0),
        0
    );
    const residual = newProductRevenue - planTotal;
    if (residual !== 0) {
        let sink = null;
        if (isRatable) {
            const lastDriverPeriod = Object.keys(driverByPeriod)
                .filter((p) => driverByPeriod[p])
                .sort()
                .pop();
            const periodEntries =
                lastDriverPeriod && entriesByPeriod[lastDriverPeriod];
            if (periodEntries) {
                // Revenue for quantity that is still undelivered belongs to the
                // item, not to a delivery. exportPSD skips plan elements with no
                // serviceDeliveryId, so an untagged sink holds the residual in
                // Unearned without reporting it as delivery revenue. Only a
                // material, positive residual earns that treatment: float dust
                // and over-allocation keep the sink they have always had.
                const needsUntaggedSink =
                    residual > 0 &&
                    !Utils.approxeq(residual, 0) &&
                    newRevenuePlan.some((p) => p.serviceDeliveryId);

                sink = needsUntaggedSink
                    ? periodEntries.find((e) => !e.serviceDeliveryId) ||
                      insertResidualElement(newRevenuePlan, periodEntries)
                    : periodEntries[0];
            }
        }
        if (!sink) sink = newRevenuePlan[newRevenuePlan.length - 1];
        sink.planAmount = (sink.planAmount || 0) + residual;
    }
}

/**
 * Adds an element to carry true-up residual in a period whose entries are all
 * delivery-tagged. newRevenuePlan is sorted by period before catch-down and
 * nothing re-sorts it afterwards, so the element is spliced in behind the
 * period's last entry rather than pushed. Shape matches the term-end leftover
 * updatePSPlanRatable builds: an amount, a period, and no delivery behind it.
 */
function insertResidualElement(newRevenuePlan, periodEntries) {
    const last = periodEntries[periodEntries.length - 1];
    const element = {
        planAmount: 0,
        actgPeriod: last.actgPeriod,
        effectivePeriod: last.actgPeriod.period,
        unitShare: 0,
        isTrueUpCatchDown: true
    };
    newRevenuePlan.splice(newRevenuePlan.indexOf(last) + 1, 0, element);
    periodEntries.push(element);
    return element;
}

/**
 * The quantity and revenue in force from each period a true-up takes effect,
 * starting with the order's own (fromPeriod null). A true-up with no period
 * takes effect in the current period; a true-up that leaves the basis as it was
 * starts no new basis.
 */
function trueUpBasesByPeriod({
    quantity,
    extendedSalePrice,
    productRevenue,
    trueUpLogs,
    currentPeriod,
    organization,
    orgConfig,
    calendarConfig
}) {
    const periodOf = (log) =>
        trueUpEffectivePeriod(log, calendarConfig) || currentPeriod;
    const bases = [
        { fromPeriod: null, quantity: Number(quantity) || 0, revenue: productRevenue }
    ];
    [...new Set(trueUpLogs.map(periodOf))].sort().forEach((period) => {
        const folded = foldTrueUpQuantityAndExtended(
            quantity,
            extendedSalePrice,
            trueUpLogs.filter((l) => !Period.isAfter(periodOf(l), period)),
            organization,
            orgConfig
        );
        const revenue = productRevenue + folded.deltaVariableAmount;
        const prior = bases[bases.length - 1];
        if (
            folded.quantity === prior.quantity &&
            Utils.approxeq(revenue, prior.revenue)
        ) {
            return;
        }
        bases.push({ fromPeriod: period, quantity: folded.quantity, revenue });
    });
    return bases;
}

/**
 * Books every period on the basis in force there. Where the basis changes, each
 * Service Delivery ID, and the untagged residual, catches up in that period to
 * what the new basis gives it through then, so earlier periods keep what their
 * own basis booked and the plan totals the last basis. The catch-up is negative
 * for a delivery that a true-down makes overage. The plan depends only on the
 * logs, not on the period it is built in, so a true-up effective in a later
 * period is booked there without the order being processed again.
 *
 * basisAmounts maps each plan element to its amount under every basis.
 */
function scheduleTrueUpsByDelivery({
    newRevenuePlan,
    bases,
    basisAmounts,
    calendarConfig
}) {
    const basisIndexAt = (period) =>
        bases.reduce(
            (index, basis, i) =>
                basis.fromPeriod && !Period.isAfter(basis.fromPeriod, period)
                    ? i
                    : index,
            0
        );

    const entriesByPeriod = {};
    newRevenuePlan.forEach((p) => {
        const period = p.actgPeriod.period;
        if (!entriesByPeriod[period]) entriesByPeriod[period] = [];
        entriesByPeriod[period].push(p);
        p.isTrueUpCatchDown = true;
    });
    const periods = [
        ...new Set([
            ...Object.keys(entriesByPeriod),
            ...bases.slice(1).map((b) => b.fromPeriod)
        ])
    ].sort();

    // Per delivery (null for untagged): the running total under every basis,
    // the running total booked, and the last element holding its amounts.
    const entitled = new Map();
    const booked = new Map();
    const lastElement = new Map();
    periods.forEach((period) => {
        const index = basisIndexAt(period);
        const bookedHere = new Map();
        (entriesByPeriod[period] || []).forEach((e) => {
            const amounts = basisAmounts.get(e);
            if (!amounts) return;
            const id = e.serviceDeliveryId || null;
            if (!entitled.has(id)) {
                entitled.set(id, amounts.map(() => 0));
                booked.set(id, 0);
            }
            const sums = entitled.get(id);
            amounts.forEach((amount, i) => {
                sums[i] += amount;
            });
            e.planAmount = amounts[index];
            bookedHere.set(id, (bookedHere.get(id) || 0) + amounts[index]);
            lastElement.set(id, e);
        });
        entitled.forEach((sums, id) => {
            let bookedThrough = booked.get(id) + (bookedHere.get(id) || 0);
            const catchUp = sums[index] - bookedThrough;
            // Rounding goes on an element the delivery already has here; only
            // a material catch-up adds one.
            const hasElement = (entriesByPeriod[period] || []).some(
                (e) => (e.serviceDeliveryId || null) === id
            );
            if (catchUp && (hasElement || !Utils.approxeq(catchUp, 0))) {
                const element = deliveryElementInPeriod(
                    newRevenuePlan,
                    entriesByPeriod,
                    period,
                    id,
                    calendarConfig
                );
                element.planAmount = (element.planAmount || 0) + catchUp;
                bookedThrough += catchUp;
                lastElement.set(id, element);
            }
            booked.set(id, bookedThrough);
        });
    });

    // Rounding still unbooked stays with the delivery, so each totals exactly
    // what the last basis gives it.
    const lastIndex = bases.length - 1;
    entitled.forEach((sums, id) => {
        const remainder = sums[lastIndex] - booked.get(id);
        if (remainder) lastElement.get(id).planAmount += remainder;
    });
}

/**
 * Returns the period's first element for a Service Delivery ID, or its first
 * untagged element when serviceDeliveryId is null, inserting one when there is
 * none. Like insertResidualElement, the element is spliced behind the period's
 * last entry because nothing re-sorts the plan after catch-down; that also
 * keeps it off index 0, the ratable residual sink.
 */
function deliveryElementInPeriod(
    newRevenuePlan,
    entriesByPeriod,
    period,
    serviceDeliveryId,
    calendarConfig
) {
    if (!entriesByPeriod[period]) entriesByPeriod[period] = [];
    const entries = entriesByPeriod[period];
    const existing = entries.find((e) =>
        serviceDeliveryId
            ? e.serviceDeliveryId === serviceDeliveryId
            : !e.serviceDeliveryId
    );
    if (existing) return existing;

    const last = entries[entries.length - 1];
    const element = {
        planAmount: 0,
        actgPeriod: last
            ? last.actgPeriod
            : Period.toActgPeriod(period, calendarConfig),
        effectivePeriod: period,
        unitShare: 0,
        isTrueUpCatchDown: true
    };
    if (serviceDeliveryId) element.serviceDeliveryId = serviceDeliveryId;
    let index;
    if (last) {
        index = newRevenuePlan.indexOf(last) + 1;
    } else {
        index = newRevenuePlan.findIndex((p) =>
            Period.isBefore(period, p.actgPeriod?.period || p.period)
        );
        if (index < 0) index = newRevenuePlan.length;
    }
    newRevenuePlan.splice(index, 0, element);
    entries.push(element);
    return element;
}

/**
 * Update to a base PS plan that recognizes the entire amount
 * in the last period and nothing before that. This plan
 * will be subsequently updated when service delivery logs
 * are gathered.
 *
 * @param plan
 * @param productRevenue
 */
export function updateToBaseServicePlan(plan, productRevenue) {
    plan.forEach((p) => {
        p.planAmount = 0.0;
        p.percentRecognized = 0.0;

        delete p.cummulativeTotal;
        delete p.cummulativePercentRecognized;
    });

    if (plan.length > 0) {
        plan[plan.length - 1].percentRecognized = 1.0;
        plan[plan.length - 1].planAmount = productRevenue;
    }
    //a default plan with no amounts in it for proportional performance
    //recognition as service delivery records are consumed
}

function getBaseProductCodesWithPoTrackBaseUsage(salesOrder) {
    const baseCodes = new Set();
    if (!salesOrder?.salesOrderItem) {
        return baseCodes;
    }
    for (const soi of salesOrder.salesOrderItem) {
        const product = soi.product;
        if (
            product?.entityType === "PO" &&
            product.trackBaseProductUsage &&
            product.parent
        ) {
            baseCodes.add(product.parent);
        }
    }
    return baseCodes;
}

function parentBaseHasProductRevenue(product, revenueArrangement) {
    if (!product?.parent || !revenueArrangement?.revenueArrangementItem) {
        return false;
    }
    const baseRai = revenueArrangement.revenueArrangementItem.find((rai) => {
        const raiProduct = rai.salesOrderItem?.product;
        return (
            raiProduct &&
            raiProduct.entityType !== "PO" &&
            raiProduct.code === product.parent
        );
    });
    return !!(baseRai && baseRai.productRevenue);
}

function shouldSuppressPsd12ForZeroRevenueBaseProduct(
    revenueArrangementItem,
    salesOrder,
    revenueArrangement
) {
    const product = revenueArrangementItem.salesOrderItem?.product;
    if (!product) {
        return false;
    }

    // Zero-revenue PO that tracks base usage while parent BASE holds the revenue
    // (e.g. SALE_PRICE left on BASE; logs cloned onto PO via trackBaseProductUsage).
    if (product.entityType === "PO" && product.trackBaseProductUsage) {
        return parentBaseHasProductRevenue(product, revenueArrangement);
    }

    // Zero-revenue BASE whose child PO tracks base usage.
    return getBaseProductCodesWithPoTrackBaseUsage(salesOrder).has(
        product.code
    );
}

/**
 * Validate and discard delivery logs if productRevenue is zero
 *
 * @param revenueArrangementItem
 * @param salesOrder
 * @param orgConfig
 * @param errors
 * @param productRevenue - The productRevenue to check (may be modified for delayed revenue)
 * @returns {boolean} true if logs were discarded (productRevenue is zero), false otherwise
 */
export function validateDeliveryLogs(
    revenueArrangementItem,
    salesOrder,
    orgConfig,
    errors,
    productRevenue,
    revenueArrangement = null
) {
    // Only discard if productRevenue is zero
    if (!productRevenue || productRevenue === 0) {
        const suppressPsd12 = shouldSuppressPsd12ForZeroRevenueBaseProduct(
            revenueArrangementItem,
            salesOrder,
            revenueArrangement
        );
        const psdByProductCode = isPsdMatchByProductOrFields(orgConfig);

        if (psdByProductCode && revenueArrangementItem.deliveryLogs) {
            // Add errors for discarded logs
            revenueArrangementItem.deliveryLogs.forEach((log) => {
                if (errors && !suppressPsd12) {
                    errors.add(Codes.PSD_12, {
                        salesOrderId: salesOrder.id,
                        salesOrderItemId:
                            revenueArrangementItem.salesOrderItemId,
                        unitsDelivered: log.unitsDelivered,
                        logDate: log.logDate,
                        serviceDeliveryId: log.serviceDeliveryId
                    });
                }
            });
        } else if (salesOrder.professionalServiceDelivery) {
            // For non-psdByProductCode, discard logs from professionalServiceDelivery
            const itemId =
                revenueArrangementItem.salesOrderItem.rootId ||
                revenueArrangementItem.salesOrderItem.id;
            const soiPSDelivery =
                salesOrder.professionalServiceDelivery.soiProfessionalServiceDelivery.find(
                    (psd) => psd.salesOrderItemId === itemId
                );
            if (soiPSDelivery && soiPSDelivery.deliveryLog) {
                // Add errors for discarded logs
                soiPSDelivery.deliveryLog.forEach((log) => {
                    if (errors && !suppressPsd12) {
                        errors.add(Codes.PSD_12, {
                            salesOrderId: salesOrder.id,
                            salesOrderItemId: itemId,
                            unitsDelivered: log.unitsDelivered,
                            logDate: log.logDate,
                            serviceDeliveryId: log.serviceDeliveryId
                        });
                    }
                });
            }
        }
    }
}

/**
 * Update the plan based on service delivery information that is available
 *
 * @param revenueArrangementItem
 * @param salesOrder
 * @param revenueArrangement
 * @param errors
 * @param enableBetaFeatures
 */
export function updatePSPlan(
    revenueArrangementItem,
    salesOrder,
    revenueArrangement,
    errors,
    enableBetaFeatures,
    calendarConfig,
    incrementalPercentComplete,
    orgConfig,
    revenueWithPPA = false,
    computePreModPlan = false
) {
    let productRevenue = revenueArrangementItem.productRevenue;

    if (salesOrder.revenueArrangement) {
        const currentRevenueArrangement = salesOrder.revenueArrangement.find(
            (ra) => ra.id === revenueArrangementItem.revenueArrangementId
        );

        if (
            currentRevenueArrangement &&
            currentRevenueArrangement.isRevenueDelayed
        ) {
            // revenue needs to be delayed
            productRevenue = 0;
        }
    }

    // Validate and discard delivery logs if productRevenue is zero
    validateDeliveryLogs(
        revenueArrangementItem,
        salesOrder,
        orgConfig,
        errors,
        productRevenue,
        revenueArrangement
    );

    const pspPlanRatable =
        revenueArrangementItem.ssp &&
        revenueArrangementItem.ssp.deliveryRecognitionMethod == "RATABLE";

    const _planPath =
        revenueArrangementItem.recognitionRule.id ===
        RecognitionRule.PERCENT_OF_COMPLETE
            ? "PERCENT_OF_COMPLETE"
            : !enableBetaFeatures
            ? "default"
            : pspPlanRatable
            ? "ratable"
            : "proportionalPerformance";

    updateToBaseServicePlan(revenueArrangementItem.plan, productRevenue);
    //a default plan with no amounts in it for proportional performance
    //recognition as service delivery records are consumed

    if (
        revenueArrangementItem.recognitionRule.id ===
        RecognitionRule.PERCENT_OF_COMPLETE
    ) {
        updatePSPlanPercentageOfCompletion(
            revenueArrangementItem,
            salesOrder,
            errors,
            productRevenue,
            calendarConfig
        );
    } else if (!enableBetaFeatures) {
        updatePSPlan_default(
            revenueArrangementItem,
            salesOrder,
            errors,
            productRevenue,
            calendarConfig
        );
    } else if (pspPlanRatable) {
        updatePSPlanRatable(
            revenueArrangementItem,
            salesOrder,
            errors,
            productRevenue,
            calendarConfig,
            incrementalPercentComplete,
            orgConfig,
            revenueWithPPA,
            revenueArrangement
        );
    } else {
        updatePSPlanProportionalPerformance(
            revenueArrangementItem,
            salesOrder,
            revenueArrangement,
            errors,
            productRevenue,
            calendarConfig,
            incrementalPercentComplete,
            orgConfig,
            revenueWithPPA,
            computePreModPlan
        );
    }
}

export function getDisabledPSDEntries(psdLogs) {
    const toReturn = [];

    psdLogs.forEach((log) => {
        if (log.disabled != undefined && log.disabled == true) {
            toReturn.push(log);
        }
    });

    toReturn.sort(function (log1, log2) {
        return moment(log1.period, Utils.PERIOD_FORMAT).diff(
            moment(log2.period, Utils.PERIOD_FORMAT)
        );
    });

    return toReturn;
}

export function getDeliveryLogByPeriod(soiPSDelivery) {
    function getLogsAccumulatedByPeriod(deliveryLog) {
        const groupedByPeriod = Utils.groupByOnArray(
            deliveryLog,
            (E) => E.period
        );
        const psdLogs = [];
        Object.keys(groupedByPeriod).forEach((period) => {
            const psdLog = groupedByPeriod[period];
            const psd = {};
            Object.assign(psd, psdLog[0], {
                unitsDelivered: undefined,
                percentComplete: undefined
            });
            psdLog.forEach((log) => {
                if (log["unitsDelivered"]) {
                    if (psd["unitsDelivered"] == undefined) {
                        psd["unitsDelivered"] = 0;
                    }
                    psd["unitsDelivered"] += log["unitsDelivered"];
                } else if (log["percentComplete"]) {
                    if (psd["percentComplete"] == undefined) {
                        psd["percentComplete"] = 0;
                    }
                    psd["percentComplete"] += log["percentComplete"];
                }
            });

            psdLogs.push(psd);
        });

        psdLogs.sort(function (log1, log2) {
            return moment(log1.period, Utils.PERIOD_FORMAT).diff(
                moment(log2.period, Utils.PERIOD_FORMAT)
            );
        });

        return psdLogs;
    }

    let deliveryLogByPeriod = undefined;
    if (soiPSDelivery.isLegacy == undefined || soiPSDelivery.isLegacy == true) {
        deliveryLogByPeriod = Utils.arrayToObject(
            soiPSDelivery.deliveryLog,
            (e) => e.period
        );
    } else {
        const psdLogs = getLogsAccumulatedByPeriod(soiPSDelivery.deliveryLog);
        //accumulate logs by period

        let previousPSD = 0;
        psdLogs.forEach((currentPsd, index) => {
            if (currentPsd["unitsDelivered"] !== undefined) {
                currentPsd["unitsDelivered"] += previousPSD;
                previousPSD = currentPsd["unitsDelivered"];
            }
        });
        //create a running total for each period

        deliveryLogByPeriod = Utils.arrayToObject(psdLogs, (E) => E.period);
    }

    return deliveryLogByPeriod;
}

export function updatePSPlan_default(
    revenueArrangementItem,
    salesOrder,
    errors,
    productRevenue,
    calendarConfig
) {
    let { professionalServiceDelivery } = salesOrder;

    if (professionalServiceDelivery) {
        let { salesOrderItem, plan } = revenueArrangementItem;
        let soiPSDelivery =
            professionalServiceDelivery.soiProfessionalServiceDelivery.find(
                (psd) => {
                    let itemId =
                        revenueArrangementItem.salesOrderItem.rootId ||
                        revenueArrangementItem.salesOrderItem.id;
                    if (psd.salesOrderItemId === itemId) return psd;
                }
            );

        if (soiPSDelivery) {
            let deliveryLogByPeriod = getDeliveryLogByPeriod(
                soiPSDelivery,
                calendarConfig
            );

            let cumulativePercentRecognized = 0;
            for (let planElement of plan) {
                const period =
                    planElement.actgPeriod?.period || planElement.period;
                if (!period) throw new Error("Bad plan state: missing period");

                if (cumulativePercentRecognized === 1) {
                    // we have recognized everything 100%
                    break;
                }

                const deliveryLog = deliveryLogByPeriod[period];
                let percentRecognized = 0.0;
                if (deliveryLog) {
                    let { unitsDelivered, percentComplete } = deliveryLog;

                    if (percentComplete) {
                        percentComplete = isNaN(percentComplete)
                            ? 0.0
                            : Math.abs(percentComplete);
                    } else {
                        unitsDelivered = isNaN(unitsDelivered)
                            ? 0.0
                            : Math.abs(unitsDelivered);
                    }

                    if (percentComplete) {
                        percentRecognized = deliveryLog.percentComplete;
                        if (percentRecognized > 1) {
                            percentRecognized = 1;
                            if (errors) {
                                errors.add(Codes.TRAN_25, {
                                    salesOrder,
                                    salesOrderItem,
                                    unitsLogPosted: deliveryLog.percentComplete,
                                    actualUnitsSold: 1,
                                    period: deliveryLog.period,
                                    extraUnits: deliveryLog.percentComplete - 1
                                });
                            }
                        }
                    } else {
                        if (
                            isNaN(unitsDelivered) ||
                            isNaN(salesOrderItem.quantity)
                        )
                            throw new Error("Invalid Units Delivered");

                        if (unitsDelivered > salesOrderItem.quantity) {
                            if (errors) {
                                errors.add(Codes.TRAN_25, {
                                    salesOrder,
                                    salesOrderItem,
                                    period: deliveryLog.period,
                                    actualUnitsSold: salesOrderItem.quantity,
                                    unitsLogPosted: unitsDelivered,
                                    extraUnits:
                                        unitsDelivered - salesOrderItem.quantity
                                });
                            }
                            unitsDelivered = salesOrderItem.quantity;
                        }
                        percentRecognized =
                            unitsDelivered / salesOrderItem.quantity;
                    }

                    percentRecognized -= cumulativePercentRecognized;
                }

                if (isNaN(percentRecognized)) throw new Error("I am dead");

                planElement.percentRecognized = percentRecognized;

                cumulativePercentRecognized += percentRecognized;
            }

            updateRatablePlan(plan, productRevenue);
        }
    }
}

export function updatePSPlanPercentageOfCompletion(
    revenueArrangementItem,
    salesOrder,
    errors,
    productRevenue
) {
    let { professionalServiceDelivery } = salesOrder;

    if (professionalServiceDelivery) {
        let soiPSDelivery =
            professionalServiceDelivery.soiProfessionalServiceDelivery.find(
                (psd) => {
                    let itemId =
                        revenueArrangementItem.salesOrderItem.rootId ||
                        revenueArrangementItem.salesOrderItem.id;
                    if (psd.salesOrderItemId === itemId) return psd;
                }
            );

        if (soiPSDelivery) {
            let { salesOrderItem, plan: revenuePlan } = revenueArrangementItem;
            let { estimatedTotalCost, estimatedUnitCost } = salesOrderItem;

            let { deliveryLog } = soiPSDelivery;

            deliveryLog.forEach((log) => {
                if (log.unitsDelivered) {
                    log.hourlyRate = estimatedUnitCost;
                    log.costOfUnitsDelivered =
                        log.unitsDelivered * estimatedUnitCost;
                }
            });

            let groupedByPeriod = Utils.agg(
                deliveryLog,
                ["period"],
                ["period"],
                [],
                [],
                [
                    "percentComplete",
                    "unitsDelivered",
                    "costIncurred",
                    "costOfUnitsDelivered"
                ]
            );
            let logsByPeriod = Utils.arrayToObject(
                groupedByPeriod,
                (E) => E.period
            );

            for (let planElement of revenuePlan) {
                const period =
                    planElement.actgPeriod?.period || planElement.period;
                if (!period) throw new Error("Bad plan state: missing period");

                const periodPSD = logsByPeriod[period];

                if (!periodPSD) {
                    planElement.percentRecognized = 0.0;
                    continue;
                }

                planElement.percentRecognized =
                    (periodPSD.costOfUnitsDelivered + periodPSD.costIncurred) /
                    estimatedTotalCost;
            }

            updateRatablePlan(revenuePlan, productRevenue);
        }
    }
}

export function updatePSPlanProportionalPerformance(
    revenueArrangementItem,
    salesOrder,
    revenueArrangement,
    errors,
    productRevenue,
    calendarConfig,
    incrementalPercentComplete,
    orgConfig,
    revenueWithPPA = false,
    computePreModPlan = false
) {
    let { professionalServiceDelivery } = salesOrder;

    let terminatedProductRevenue = undefined;
    let soiPSDelivery = undefined;
    let cummulativePercentRecognized = 0;

    const psdByProductCode = isPsdMatchByProductOrFields(orgConfig);

    if (professionalServiceDelivery) {
        if (psdByProductCode) {
            soiPSDelivery = {
                deliveryLog: revenueArrangementItem.deliveryLogs
            };
        } else {
            soiPSDelivery =
                professionalServiceDelivery.soiProfessionalServiceDelivery.find(
                    (psd) => {
                        let itemId =
                            revenueArrangementItem.salesOrderItem.rootId ||
                            revenueArrangementItem.salesOrderItem.id;
                        if (psd.salesOrderItemId === itemId) return psd;
                    }
                );
        }

        if (soiPSDelivery && soiPSDelivery.deliveryLog) {
            let {
                salesOrderItem,
                plan: revenuePlan,
                itemType,
                unrecognizedQuantity
            } = revenueArrangementItem;
            let { salePrice, extendedSalePrice, quantity } = salesOrderItem;
            const modificationDate = revenueArrangement?.modificationDate;

            // Check for variable consideration TRUE_UP entries
            const propEnableVariableConsiderationTrueUp = orgConfig.find(
                (config) =>
                    config.id ===
                    "properties/enable_variable_consideration_true_up"
            );
            const enableVariableConsiderationTrueUpValue =
                propEnableVariableConsiderationTrueUp &&
                propEnableVariableConsiderationTrueUp.value;
            const enableVariableConsiderationTrueUp =
                enableVariableConsiderationTrueUpValue === true ||
                enableVariableConsiderationTrueUpValue === "true";

            let trueUpPreMutation = null;
            if (enableVariableConsiderationTrueUp) {
                const trueUpLogsFiltered = soiPSDelivery.deliveryLog.filter(
                    isTrueUpVariableConsiderationLog
                );
                if (trueUpLogsFiltered.length > 0) {
                    const anchorPeriod = trueUpFilterAnchorPeriod(
                        revenueArrangement,
                        calendarConfig
                    );
                    const { futureLogs } = splitTrueUpLogsByEffectivePeriod(
                        trueUpLogsFiltered,
                        anchorPeriod,
                        calendarConfig
                    );
                    const folded = foldTrueUpQuantityAndExtended(
                        quantity,
                        extendedSalePrice,
                        futureLogs,
                        salesOrder.organization,
                        orgConfig
                    );

                    salesOrderItem.variableQuantity =
                        folded.deltaVariableQuantity;
                    salesOrderItem.variableAmount = folded.deltaVariableAmount;
                    if (
                        folded.deltaVariableQuantity !== 0 ||
                        folded.deltaVariableAmount !== 0
                    ) {
                        trueUpPreMutation = {
                            oldQuantity: quantity,
                            oldProductRevenue: productRevenue,
                            trueUpLogs: futureLogs
                        };
                        quantity = folded.quantity;
                        extendedSalePrice = folded.extendedSalePrice;
                        salePrice = quantity
                            ? extendedSalePrice / quantity
                            : salePrice;
                        productRevenue =
                            productRevenue + folded.deltaVariableAmount;
                        revenueArrangementItem.productRevenue = productRevenue;
                    }
                }
            }

            let { deliveryLog } = soiPSDelivery;

            // Filter out TRUE_UP variable consideration logs from regular processing
            // These logs only affect pricing/quantity, not recognition timing
            if (enableVariableConsiderationTrueUp) {
                deliveryLog = filterOutTrueUpLogs(deliveryLog);
            }

            // This revenue item has a termination event associated with it,
            // we discard delivery logs after termination.

            if (revenueArrangementItem.terminationDate) {
                const { terminationDate } = revenueArrangementItem;

                // Discard delivery logs after termination Date
                deliveryLog = deliveryLog.filter(
                    (log) => terminationDate >= log.logDate
                );
            }

            if (computePreModPlan && modificationDate) {
                // Discard delivery logs after modification Date. So, that only frozen revenue (revenue allocated pre modification) is calculated
                const modificationPeriod = Period.toPeriod(
                    modificationDate,
                    calendarConfig
                );
                deliveryLog = deliveryLog.filter(
                    (log) =>
                        modificationDate >= log.logDate &&
                        Utils.isSameOrBefore(
                            log.effectivePeriod,
                            modificationPeriod
                        )
                );
            }

            let firstPeriod =
                revenuePlan[0].actgPeriod?.period || revenuePlan[0].period;

            if (deliveryLog.length > 0) {
                let percentCompleteMethod =
                    (!Utils.isNumber(deliveryLog[0].unitsDelivered) && true) ||
                    false;

                soiPSDelivery.logMethod = percentCompleteMethod
                    ? "percentComplete"
                    : "unitsDelivered";

                if (
                    itemType == ItemType.SPLIT_UNRECOGNIZED &&
                    modificationDate
                ) {
                    if (soiPSDelivery.logMethod === "unitsDelivered") {
                        quantity = unrecognizedQuantity;
                    }
                    const _beforeCount = deliveryLog.length;
                    // Discard delivery logs before the modification date
                    deliveryLog = deliveryLog.filter(
                        (log) => log.logDate >= modificationDate
                    );
                }

                deliveryLog = deliveryLog.sort(
                    Comparator.getComparator(
                        ["effectivePeriod", "logDate", "timestamp"],
                        [
                            Comparator.forType("period"),
                            Comparator.forType("date"),
                            Comparator.forType("number")
                        ]
                    )
                );

                if (percentCompleteMethod) {
                    let percentToDate = 0;

                    deliveryLog.forEach((log) => {
                        if (incrementalPercentComplete) {
                            // The percentage is already incremental and no need to - percentToDate
                            log.logPercentComplete = log.percentComplete;
                        } else {
                            // percent complete is the client sent percentage
                            // percentToDate is the cumulative percent complete from the last log
                            log.logPercentComplete =
                                log.percentComplete - percentToDate;
                        }

                        log.billableAmount =
                            extendedSalePrice * log.logPercentComplete;

                        percentToDate = log.percentComplete;

                        if (!log.effectivePeriod) {
                            if (log.period < firstPeriod) {
                                log.effectivePeriod = firstPeriod;
                            } else {
                                log.effectivePeriod = log.period;
                            }
                        }
                    });
                } else {
                    let remainingUnits = quantity;
                    deliveryLog.forEach((log) => {
                        let units = log.unitsDelivered;
                        log.hourlyRate = log.hourlyRate || salePrice;
                        log.billableAmount =
                            log.unitsDelivered * log.hourlyRate;
                        if (
                            remainingUnits == 0 ||
                            remainingUnits < log.unitsDelivered
                        ) {
                            units = remainingUnits;
                        }
                        remainingUnits -= units;
                        log.unitsConsumed = units;
                        if (!log.effectivePeriod) {
                            if (log.period < firstPeriod) {
                                log.effectivePeriod = firstPeriod;
                            } else {
                                log.effectivePeriod = log.period;
                            }
                        }
                    });
                }

                let groupedByEffAndPostingPeriod = Utils.agg(
                    deliveryLog,
                    ["effectivePeriod", "period"],
                    ["effectivePeriod", "period"],
                    [],
                    [],
                    ["billableAmount", "logPercentComplete", "unitsDelivered"]
                );
                let groupedByEffPeriod = Utils.groupByOnArray(
                    groupedByEffAndPostingPeriod,
                    (E) => E.effectivePeriod
                );

                let totalUnitsReconisedTillDate = 0;
                let totalPercentRecognisedTillDate = 0;

                const newRevenuePlan = [];
                for (let planElement of revenuePlan) {
                    const period =
                        planElement.actgPeriod?.period || planElement.period;
                    if (!period)
                        throw new Error("Bad plan state: missing period");

                    const psdLogs = groupedByEffPeriod[period];
                    if (!psdLogs || !psdLogs.length) {
                        planElement.percentRecognized = 0;
                        newRevenuePlan.push(planElement);
                        continue;
                    }

                    let periodPsdLogs;
                    if (revenueWithPPA == true) {
                        // To ensure that we have at least one non-prior-period-adj transaction in currenct period
                        psdLogs.push({
                            billableAmount: 0,
                            effectivePeriod: period,
                            logPercentComplete: 0,
                            period: period,
                            unitsDelivered: 0
                        });
                        periodPsdLogs = Utils.agg(
                            psdLogs,
                            ["period"],
                            ["period"],
                            [],
                            [],
                            [
                                "billableAmount",
                                "logPercentComplete",
                                "unitsDelivered"
                            ]
                        );
                    } else {
                        periodPsdLogs = Utils.agg(
                            psdLogs,
                            ["effectivePeriod"],
                            ["effectivePeriod"],
                            [],
                            [],
                            [
                                "billableAmount",
                                "logPercentComplete",
                                "unitsDelivered"
                            ]
                        );
                    }
                    const plan = planElement;
                    periodPsdLogs.forEach((periodPSD) => {
                        let planElement = Object.assign({}, plan);
                        if (revenueWithPPA == true) {
                            planElement.effectivePeriod = periodPSD.period;
                        }
                        if (soiPSDelivery.logMethod === "unitsDelivered") {
                            // if we have already recognised full no need to look in to future what happens.
                            if (totalUnitsReconisedTillDate >= quantity) {
                                planElement.percentRecognized = 0;
                            } else {
                                planElement.percentRecognized =
                                    periodPSD.unitsDelivered / quantity;
                            }
                            totalUnitsReconisedTillDate +=
                                periodPSD.unitsDelivered;
                        } else {
                            // if we have already recognised full no need to look in to future what happens.
                            if (totalPercentRecognisedTillDate >= 1) {
                                planElement.percentRecognized = 0;
                            } else {
                                planElement.percentRecognized =
                                    periodPSD.logPercentComplete;
                            }
                            totalPercentRecognisedTillDate +=
                                periodPSD.logPercentComplete;
                        }

                        cummulativePercentRecognized +=
                            planElement.percentRecognized;
                        newRevenuePlan.push(planElement);
                    });
                }
                revenuePlan = newRevenuePlan;
                revenueArrangementItem.plan = newRevenuePlan;

                if (revenueArrangementItem.terminationDate) {
                    // This item is terminated at this point.
                    // Amount that is recognized till termination date is considered to be 100% recognized.
                    revenuePlan.forEach((element) => {
                        // For e.g
                        // 202209 = 0.4 (40%) revenue and 202210 = 0.2 (20%) revenue, total revenue is 0.6 (60%)
                        // Changing to
                        // 202209 = 0.4 / 0.6 = 0.666
                        // 202210 = 0.2 / 0.6 = 0.333
                        element.percentRecognized =
                            element.percentRecognized /
                            cummulativePercentRecognized;
                    });

                    // Would only be the case when new arrangement is created.
                    if (!revenueArrangementItem.terminatedProductRevenue) {
                        revenueArrangementItem.terminatedProductRevenue =
                            revenueArrangementItem.productRevenue *
                            Math.min(cummulativePercentRecognized, 1);
                    }

                    // here product revenue is passed explictly to this function, so we infer terminatedProductRevenue
                    terminatedProductRevenue =
                        productRevenue *
                        Math.min(cummulativePercentRecognized, 1);

                    // When service log is received in an open period after termination period is closed.
                    // terminated revenue may increase or decrease.
                    if (terminatedProductRevenue) {
                        revenueArrangementItem.terminatedProductRevenue =
                            terminatedProductRevenue;
                    }
                }

                if (revenueArrangementItem.terminationDate) {
                    updateRatablePlan(revenuePlan, terminatedProductRevenue);
                } else {
                    updateRatablePlan(revenuePlan, productRevenue);
                }

                if (trueUpPreMutation) {
                    applyTrueUpCatchDown({
                        newRevenuePlan: revenuePlan,
                        organization: salesOrder.organization,
                        deliveryLog,
                        orgConfig,
                        isPercentDriver:
                            soiPSDelivery.logMethod === "percentComplete",
                        ...trueUpPreMutation
                    });
                }
            }
        }
    }

    // Termination date is present, but NO service is delivered,
    // We zero out the revenue plan in this case.
    if (!soiPSDelivery && revenueArrangementItem.terminationDate) {
        const zeroElement = {
            planAmount: 0,
            percentRecognized: 0,
            cummulativeTotal: 0,
            cummulativePercentRecognized: 0
        };
        revenueArrangementItem.plan.forEach((element) => {
            Object.assign(element, zeroElement);
        });
    }

    return {
        cummulativePercentRecognized
    };
}

export function updatePSPlanRatable(
    revenueArrangementItem,
    salesOrder,
    errors,
    productRevenue,
    calendarConfig,
    incrementalPercentComplete,
    orgConfig,
    revenueWithPPA = false,
    revenueArrangement = undefined
) {
    const { professionalServiceDelivery } = salesOrder;

    if (!professionalServiceDelivery) {
        return;
    }

    const psdByProductCode = isPsdMatchByProductOrFields(orgConfig);

    const propRevenueByServiceDeliveryId = orgConfig.find(
        (config) => config.id === "properties/revenue_by_service_delivery_id"
    );
    const revenueByServiceDeliveryId =
        (propRevenueByServiceDeliveryId &&
            propRevenueByServiceDeliveryId.value) ||
        false;

    let soiPSDelivery;
    if (psdByProductCode) {
        soiPSDelivery = {
            deliveryLog: revenueArrangementItem.deliveryLogs
        };
    } else {
        soiPSDelivery =
            professionalServiceDelivery.soiProfessionalServiceDelivery.find(
                (psd) => {
                    let itemId =
                        revenueArrangementItem.salesOrderItem.rootId ||
                        revenueArrangementItem.salesOrderItem.id;
                    if (psd.salesOrderItemId === itemId) return psd;
                }
            );
    }

    if (!soiPSDelivery || !soiPSDelivery.deliveryLog) {
        return;
    }

    let { salesOrderItem, plan, ssp } = revenueArrangementItem;
    let { quantity, product } = salesOrderItem;
    let { deliveryLog } = soiPSDelivery;
    soiPSDelivery.logMethod = "unitsDelivered";

    // Check for variable consideration TRUE_UP entries
    const propEnableVariableConsiderationTrueUp = orgConfig.find(
        (config) =>
            config.id === "properties/enable_variable_consideration_true_up"
    );
    const enableVariableConsiderationTrueUpValue =
        propEnableVariableConsiderationTrueUp &&
        propEnableVariableConsiderationTrueUp.value;
    const enableVariableConsiderationTrueUp =
        enableVariableConsiderationTrueUpValue === true ||
        enableVariableConsiderationTrueUpValue === "true";

    const currentPeriod = salesOrder.organization?.currentPeriod;
    let trueUpPreMutation = null;
    let trueUpBases = null;
    if (enableVariableConsiderationTrueUp) {
        const trueUpLogsFiltered = deliveryLog.filter(
            isTrueUpVariableConsiderationLog
        );
        if (trueUpLogsFiltered.length > 0) {
            const baseExt =
                salesOrderItem.extendedSalePrice != null &&
                !isNaN(salesOrderItem.extendedSalePrice)
                    ? Number(salesOrderItem.extendedSalePrice)
                    : 0;

            const anchorPeriod = trueUpFilterAnchorPeriod(
                revenueArrangement,
                calendarConfig
            );
            const { futureLogs } = splitTrueUpLogsByEffectivePeriod(
                trueUpLogsFiltered,
                anchorPeriod,
                calendarConfig
            );
            // With per-delivery revenue each true-up takes effect from its own
            // period (scheduleTrueUpsByDelivery). The period-level catch-up
            // instead assumes closed periods posted the new quantity, so there
            // every true-up applies at once.
            if (revenueByServiceDeliveryId && currentPeriod) {
                const bases = trueUpBasesByPeriod({
                    quantity,
                    extendedSalePrice: baseExt,
                    productRevenue,
                    trueUpLogs: futureLogs,
                    currentPeriod,
                    organization: salesOrder.organization,
                    orgConfig,
                    calendarConfig
                });
                if (bases.length > 1) trueUpBases = bases;
            }
            const folded = foldTrueUpQuantityAndExtended(
                quantity,
                baseExt,
                futureLogs,
                salesOrder.organization,
                orgConfig
            );

            salesOrderItem.variableQuantity = folded.deltaVariableQuantity;
            salesOrderItem.variableAmount = folded.deltaVariableAmount;
            if (
                folded.deltaVariableQuantity !== 0 ||
                folded.deltaVariableAmount !== 0
            ) {
                trueUpPreMutation = {
                    oldQuantity: quantity,
                    oldProductRevenue: productRevenue,
                    trueUpLogs: futureLogs
                };
                quantity = folded.quantity;
                productRevenue = productRevenue + folded.deltaVariableAmount;
                revenueArrangementItem.productRevenue = productRevenue;
            }
        }
        deliveryLog = filterOutTrueUpLogs(deliveryLog);
    }

    const getDeliveryEndDateByTerm = (deliveryStartDate, term, unit) => {
        switch (unit) {
            case "Days":
                return Utils.addDays(deliveryStartDate, term);
            case "Months":
                return Utils.addMonths(deliveryStartDate, term);
            case "Years":
                return Utils.formatDate(
                    Utils.addYearsTo(deliveryStartDate, term)
                );
        }
    };

    const firstPeriod = plan[0].actgPeriod?.period || plan[0].period;
    const firstActgPeriod = Period.toActgPeriod(firstPeriod, calendarConfig);
    const firstPeriodDate = firstActgPeriod.startDate;

    const endPeriodPlan = plan[plan.length - 1];

    const getDeliveryStartDate = (log) => {
        return log.logDate < firstPeriodDate ? firstPeriodDate : log.logDate;
    };

    const getDeliveryEndDate = (log) => {
        let deliveryEndDate;
        if (log.serviceEndDate) {
            deliveryEndDate =
                log.serviceEndDate < firstPeriodDate
                    ? firstPeriodDate
                    : log.serviceEndDate;
        } else if (ssp && ssp.deliveryTerm && ssp.unit) {
            deliveryEndDate = getDeliveryEndDateByTerm(
                log.deliveryStartDate,
                ssp.deliveryTerm,
                ssp.unit
            );
        } else if (product && product.term && product.termUnit) {
            deliveryEndDate = getDeliveryEndDateByTerm(
                log.deliveryStartDate,
                product.term,
                product.termUnit
            );
        }
        if (!deliveryEndDate && errors) {
            errors.add(Codes.TRAN_44, {
                salesOrderId: salesOrder.id,
                salesOrderItemId: salesOrderItem.id,
                quantityDelivered: log.unitsDelivered,
                logDate: log.logDate
            });
        }
        return deliveryEndDate;
    };

    let newRevenuePlan = [];
    let totalRevenueRecognized = 0;
    let remainingUnits = quantity;

    // Each element's amount under every true-up basis, built from the same
    // logs with each basis's own consumption.
    const basisAmounts = new Map();
    const basisUnitsLeft = trueUpBases && trueUpBases.map((b) => b.quantity);
    const basisRecognized = trueUpBases && trueUpBases.map(() => 0);

    const propEndDateInclusive = orgConfig.find(
        (config) => config.id === "properties/ratable_end_date_inclusive"
    );
    const endDateInclusive =
        (propEndDateInclusive && propEndDateInclusive.value) || false;

    deliveryLog = deliveryLog.filter((log) => {
        let result = true;
        if (endDateInclusive) {
            result = Period.isSameOrBefore(log.logDate, salesOrderItem.endDate);
        } else {
            result = Period.isBefore(log.logDate, salesOrderItem.endDate);
        }
        return (
            result &&
            Period.isSameOrBefore(
                log.effectivePeriod,
                endPeriodPlan.actgPeriod.period
            )
        );
    });

    deliveryLog = deliveryLog.sort(
        Comparator.getComparator(
            ["effectivePeriod", "logDate", "timestamp"],
            [
                Comparator.forType("period"),
                Comparator.forType("date"),
                Comparator.forType("number")
            ]
        )
    );

    // We want to track what the latest service period that we need to book revenue to, so keeping this variable up-to-date.
    let servicePeriodExtendedDate = salesOrderItem.endDate;

    deliveryLog.forEach((log) => {
        const item = {
            logDate: log.logDate,
            serviceEndDate: log.serviceEndDate,
            unitsDelivered: log.unitsDelivered,
            salesOrderItem: salesOrderItem
        };
        item.deliveryStartDate = getDeliveryStartDate(item);
        item.deliveryEndDate = getDeliveryEndDate(item);
        if (!item.deliveryEndDate) {
            return;
        }

        let units = log.unitsDelivered || 0;

        // If total number of uints that were send in service delivery input logs exceeds the quantity sold,
        // We ignore additional unit.
        if (remainingUnits == 0 || remainingUnits < units) {
            units = remainingUnits;
        }

        const logRevenue = (units / quantity) * productRevenue;
        totalRevenueRecognized += logRevenue;
        remainingUnits -= units;
        log.unitsConsumed = units;
        const plan = ArrangementUtils.buildPlan(
            item,
            salesOrder,
            orgConfig,
            logRevenue,
            calendarConfig
        );

        const byBasisByBuiltPeriod =
            trueUpBases &&
            trueUpBases.map((basis, i) => {
                let basisUnits = log.unitsDelivered || 0;
                if (basisUnitsLeft[i] == 0 || basisUnitsLeft[i] < basisUnits) {
                    basisUnits = basisUnitsLeft[i];
                }
                basisUnitsLeft[i] -= basisUnits;
                const basisLogRevenue = basis.quantity
                    ? (basisUnits / basis.quantity) * basis.revenue
                    : 0;
                basisRecognized[i] += basisLogRevenue;
                const byPeriod = {};
                ArrangementUtils.buildPlan(
                    item,
                    salesOrder,
                    orgConfig,
                    basisLogRevenue,
                    calendarConfig
                ).forEach((op) => {
                    const k = op.actgPeriod.period;
                    byPeriod[k] = (byPeriod[k] || 0) + (op.planAmount || 0);
                });
                return byPeriod;
            });

        plan.forEach((p) => {
            // Keyed on the period buildPlan produced, before PPA re-anchoring
            // below can move the element into log.effectivePeriod. A period's
            // amount goes to its first element only.
            if (byBasisByBuiltPeriod) {
                const k = p.actgPeriod.period;
                basisAmounts.set(
                    p,
                    byBasisByBuiltPeriod.map((byPeriod) => {
                        const amount = byPeriod[k] || 0;
                        delete byPeriod[k];
                        return amount;
                    })
                );
            }
            if (Period.isBefore(p.actgPeriod.period, log.effectivePeriod)) {
                if (revenueWithPPA) {
                    p.effectivePeriod = p.actgPeriod.period;
                }
                p.actgPeriod = Period.toActgPeriod(
                    log.effectivePeriod,
                    calendarConfig
                );
            }
            if (revenueByServiceDeliveryId) {
                p.serviceDeliveryId = log.serviceDeliveryId;
            }

            // Delivered volume behind this element. Written unconditionally so
            // that "no element carries the field" can only mean "this is not the
            // ratable path"; applyTrueUpCatchDown splits each period target by it.
            p.unitShare = logRevenue
                ? ((p.planAmount || 0) / logRevenue) * units
                : 0;
            const planEffectiveDate = p.actgPeriod.effectiveDate;
            // servicePeriodExtendedDate need to be the lates of delivery end date of all the logs.
            servicePeriodExtendedDate =
                planEffectiveDate > servicePeriodExtendedDate
                    ? planEffectiveDate
                    : servicePeriodExtendedDate;

            newRevenuePlan.push(p);
        });
    });

    // We have logs that have service period that goes beyond the deliveryEndDate for the order item and arrangement.
    // Let remember the extended date so we can use it later when generating the transactions.
    if (servicePeriodExtendedDate > revenueArrangementItem.deliveryEndDate) {
        revenueArrangementItem.servicePeriodExtendedDate =
            servicePeriodExtendedDate;
    }

    // All remaining revenue will be taken in last month,
    // If there is not remaining revenue we do not need to add this.
    // Any true-up basis may leave revenue there, and it is booked like a
    // delivery when the basis changes.
    const basisResiduals =
        trueUpBases &&
        trueUpBases.map((b, i) => b.revenue - basisRecognized[i]);
    const hasBasisResidual =
        !!basisResiduals && basisResiduals.some((r) => !Utils.approxeq(r, 0));
    if (totalRevenueRecognized != productRevenue || hasBasisResidual) {
        const leftover = {
            planAmount: endPeriodPlan.planAmount - totalRevenueRecognized,
            actgPeriod: endPeriodPlan.actgPeriod,
            effectivePeriod: endPeriodPlan.actgPeriod.period,
            unitShare: 0
        };
        if (basisResiduals) basisAmounts.set(leftover, basisResiduals);
        newRevenuePlan.push(leftover);
    }

    // Now let fill the gaps, this is where we add planElement for months where there was no delivery.
    // They are all zero unit and zero revenue planElements.
    // Optimize: Use Set for O(1) lookups instead of find() which is O(n)
    // For 40-year orders (480 periods), this changes from O(n²) to O(n)
    const existingPeriods = new Set();
    for (const p of newRevenuePlan) {
        existingPeriods.add(p.actgPeriod.period);
    }

    for (const p1 of plan) {
        if (!existingPeriods.has(p1.actgPeriod.period)) {
            newRevenuePlan.push({
                planAmount: 0,
                actgPeriod: p1.actgPeriod,
                effectivePeriod: p1.actgPeriod.period,
                unitShare: 0
            });
        }
    }

    newRevenuePlan = newRevenuePlan.sort(
        Comparator.getComparator(
            [(element) => element.actgPeriod.period],
            [Comparator.forType("period")]
        )
    );

    if (trueUpBases) {
        scheduleTrueUpsByDelivery({
            newRevenuePlan,
            bases: trueUpBases,
            basisAmounts,
            calendarConfig
        });

        // A catch-up can land in a period after the service term; transactions
        // must still be generated for it.
        const lastEffectiveDate = newRevenuePlan.reduce(
            (latest, p) =>
                p.actgPeriod?.effectiveDate > latest
                    ? p.actgPeriod.effectiveDate
                    : latest,
            servicePeriodExtendedDate
        );
        if (lastEffectiveDate > revenueArrangementItem.deliveryEndDate) {
            revenueArrangementItem.servicePeriodExtendedDate = lastEffectiveDate;
        }
    } else if (trueUpPreMutation) {
        applyTrueUpCatchDown({
            newRevenuePlan,
            organization: salesOrder.organization,
            deliveryLog,
            isRatable: true,
            orgConfig,
            ...trueUpPreMutation
        });
    }

    revenueArrangementItem.plan = newRevenuePlan;
}

function buildDeletedServiceItemResolver(salesOrder) {
    const deletedItemKeys = new Set();
    const activeItemKeys = new Set();
    for (const soi of salesOrder?.salesOrderItem || []) {
        const keys = [soi.id, soi.rootId].filter((k) => k != null);
        for (const k of keys) {
            if (soi.isDeleted === true) deletedItemKeys.add(k);
            else activeItemKeys.add(k);
        }
    }
    for (const k of activeItemKeys) deletedItemKeys.delete(k);
    return (serviceItem) => {
        if (serviceItem?.salesOrderItem?.isDeleted === true) return true;
        const keys = [
            serviceItem?.salesOrderItemId,
            serviceItem?.salesOrderItem?.id,
            serviceItem?.salesOrderItem?.rootId
        ].filter((k) => k != null);
        return keys.some((k) => deletedItemKeys.has(k));
    };
}

export const buildServiceDeliveryRollForward = (
    jobId,
    salesOrder,
    org,
    calendarConfig,
    incrementalPercentConfig,
    isLegacy,
    orgConfig
) => {
    salesOrder.revenueArrangement.sort((ra1, ra2) =>
        ra2.effectiveDate < ra1.effectiveDate ? 1 : -1
    );

    const isServiceItemDeleted = buildDeletedServiceItemResolver(salesOrder);

    //order arrangements by effective date
    function deliveryLog(salesOrderItem, purchases, raiDeliveryLogs) {
        const { incrementalPurchases } = purchases;
        const percentageToUnitsLog = {};
        const log = {};

        const psdByProductCode = isPsdMatchByProductOrFields(orgConfig);

        let d;
        // If psdByProductCode is enabled, get the logs for the item from revenueArrangementItem
        if (psdByProductCode) {
            if (raiDeliveryLogs) {
                d = {
                    logMethod: "unitsDelivered",
                    deliveryLog: raiDeliveryLogs
                };
            }
        } else {
            d =
                salesOrder.professionalServiceDelivery?.soiProfessionalServiceDelivery?.find(
                    (soiPS) => soiPS.salesOrderItemId === salesOrderItem.id
                );
        }

        if (!d) return { log, percentageToUnitsLog };

        let { deliveryLog, logMethod } = cloneDeep(d);

        const propEnableVariableConsiderationTrueUp = orgConfig.find(
            (config) =>
                config.id === "properties/enable_variable_consideration_true_up"
        );
        const enableVariableConsiderationTrueUpValue =
            propEnableVariableConsiderationTrueUp &&
            propEnableVariableConsiderationTrueUp.value;
        const enableVariableConsiderationTrueUp =
            enableVariableConsiderationTrueUpValue === true ||
            enableVariableConsiderationTrueUpValue === "true";
        if (enableVariableConsiderationTrueUp) {
            deliveryLog = filterOutTrueUpLogs(deliveryLog);
        }

        let priorPeriod;
        const day1Log = deliveryLog[0];
        if (day1Log) {
            const logPeriod = day1Log.effectivePeriod || day1Log.period;
            const _soItemStartActgPeriod = Period.toActgPeriod(
                salesOrderItem.startDate,
                calendarConfig
            );
            if (logPeriod > _soItemStartActgPeriod.period) {
                // I have gap in the deliveryLogs
                const gapPeriods = Period.periodsBetween(
                    _soItemStartActgPeriod.period,
                    logPeriod,
                    true,
                    false,
                    calendarConfig || { type: "default" }
                );

                for (const gp of gapPeriods) {
                    const logToPopulate = Utils.cloneDeep(day1Log);

                    const periodStartDate = Period.toStartOfMonth(gp);

                    logToPopulate.effectivePeriod = gp;
                    logToPopulate.period = gp;
                    logToPopulate.logDate = periodStartDate;
                    logToPopulate.percentComplete = 0.0;
                    logToPopulate.logPercentComplete = 0.0;
                    logToPopulate.billableAmount = 0;
                    logToPopulate.unitsDelivered = 0;
                    logToPopulate.id = shortId.generate();
                    logToPopulate.timestamp =
                        new Date(periodStartDate).getTime() / 1000;

                    deliveryLog.push(logToPopulate);
                }

                deliveryLog = deliveryLog.sort(
                    Comparator.getComparator(
                        ["effectivePeriod", "logDate", "timestamp"],
                        [
                            Comparator.forType("period"),
                            Comparator.forType("date"),
                            Comparator.forType("number")
                        ]
                    )
                );
            }
        }

        let currentQuantity = 0.0;
        // let quantityTillLastPeriod = 0.0
        // let index = 0
        for (let d of deliveryLog) {
            const period = d.effectivePeriod || d.period;
            if (!log[period]) log[period] = 0.0;

            if (logMethod == "percentComplete") {
                if (incrementalPurchases[period] && period != priorPeriod) {
                    currentQuantity += incrementalPurchases[period];
                    priorPeriod = period;
                }

                // always converting percetage to units
                if (incrementalPercentConfig) {
                    log[period] += Number(d.logPercentComplete);
                } else {
                    log[period] = Number(d.logPercentComplete);
                }

                percentageToUnitsLog[period] = log[period] * currentQuantity;
            } else {
                log[period] += Number(d.unitsDelivered);
            }
        }

        return { log, percentageToUnitsLog };
    }

    function getLogMethod(salesOrderItem) {
        const psdByProductCode = isPsdMatchByProductOrFields(orgConfig);

        if (psdByProductCode) {
            return "unitsDelivered";
        }

        const d =
            salesOrder.professionalServiceDelivery?.soiProfessionalServiceDelivery?.find(
                (soiPS) => soiPS.salesOrderItemId === salesOrderItem.id
            );

        if (d) {
            return d.logMethod;
        }
    }

    function purchaseLog(serviceItem, logMethod) {
        let incrementalPurchases = {};
        const incrementalPurchasesByPercentage = {};
        const isDeleted = isServiceItemDeleted(serviceItem);
        // if this is modification of an order, we will have incremental purchases available at the so service item level
        const soServiceItem =
            salesOrder.professionalServiceDelivery?.soiProfessionalServiceDelivery?.find(
                (soiPS) =>
                    soiPS.salesOrderItemId === serviceItem.salesOrderItemId
            );
        if (soServiceItem && soServiceItem.incrementalPurchases && !isDeleted) {
            incrementalPurchases = soServiceItem.incrementalPurchases;
        } else {
            // we don't have incrementalPurchases available at the service line item.
            let cumulativeQuantity = 0;
            for (let arrangement of salesOrder.revenueArrangement) {
                const effectiveActgPeriod = Period.toActgPeriod(
                    arrangement.effectiveDate,
                    calendarConfig
                );
                const item = arrangement.revenueArrangementItem?.find(
                    (a) => a.salesOrderItemId === serviceItem.salesOrderItemId
                );
                if (item) {
                    incrementalPurchases[effectiveActgPeriod.period] =
                        item.salesOrderItem.quantity - cumulativeQuantity;
                    cumulativeQuantity = item.salesOrderItem.quantity;
                }
            }
        }

        const propEnableVariableConsiderationTrueUp = orgConfig.find(
            (config) =>
                config.id === "properties/enable_variable_consideration_true_up"
        );
        const enableVariableConsiderationTrueUpValue =
            propEnableVariableConsiderationTrueUp &&
            propEnableVariableConsiderationTrueUp.value;
        const enableVariableConsiderationTrueUp =
            enableVariableConsiderationTrueUpValue === true ||
            enableVariableConsiderationTrueUpValue === "true";

        if (enableVariableConsiderationTrueUp && !isDeleted) {
            const deliveryLogs =
                soServiceItem?.deliveryLog || serviceItem.deliveryLogs || [];
            applyTrueUpQuantityToIncrementalPurchases(
                incrementalPurchases,
                deliveryLogs,
                org,
                orgConfig,
                calendarConfig
            );
        }

        let priorClosedPeriods = {};
        if (soServiceItem && soServiceItem.priorClosedPeriods) {
            priorClosedPeriods = soServiceItem.priorClosedPeriods;
        }

        if (logMethod == "percentComplete") {
            // for percentage complete, we need quantity in percentage
            // this way we can calculate the correct numbers
            let priorCumulativeQuantity = 0;
            // Sort chronologically — true-ups can insert period keys out of insertion order.
            const periodKeys = Object.keys(incrementalPurchases).sort(
                Comparator.forType("period")
            );
            periodKeys.forEach((p, i) => {
                if (i > 0) {
                    incrementalPurchasesByPercentage[p] =
                        incrementalPurchases[p] /
                        (priorCumulativeQuantity + incrementalPurchases[p]);
                } else {
                    // on day1 it's always 100%
                    incrementalPurchasesByPercentage[p] = 1;
                }

                priorCumulativeQuantity += incrementalPurchases[p];
            });

            // for percentage, I need quantity as well as percentages of quantities
            return {
                incrementalPurchasesByPercentage,
                incrementalPurchases,
                priorClosedPeriods
            };
        }

        // for units delivered, I only need quantity
        return {
            incrementalPurchasesByPercentage,
            incrementalPurchases,
            priorClosedPeriods
        };
    }

    const currentRevenueArrangement = salesOrder.currentRevenueArrangement;

    const rollForward = [];
    const serviceItems =
        currentRevenueArrangement?.revenueArrangementItem.filter(
            (item) =>
                item.recognitionRule.id ===
                    RecognitionRule.PROPORTIONAL_PERFORMANCE ||
                item.recognitionRule.id === RecognitionRule.PERCENT_OF_COMPLETE
        ) || [];

    for (let serviceItem of serviceItems) {
        const {
            plan,
            salesOrderItem,
            deliveryEndDate,
            servicePeriodExtendedDate,
            deliveryLogs: raiDeliveryLogs
        } = serviceItem;
        const logMethod = getLogMethod(salesOrderItem);

        const purchases = purchaseLog(serviceItem, logMethod);
        const { log, percentageToUnitsLog } = deliveryLog(
            salesOrderItem,
            purchases,
            raiDeliveryLogs
        );

        // When we have prior-period-adjustment we will have multiple entries on same actg period
        // with different effective period so to ensure that we have at most 1 entry for each actg period
        let servicePeriods = Array.from(
            new Set(plan.map((p) => p.actgPeriod.period))
        );

        // filter out periods after delivery end date
        const endPeriod = Period.toPeriod(deliveryEndDate);
        servicePeriods = servicePeriods.filter((p) => p <= endPeriod);

        const deliveryMethod =
            logMethod == "unitsDelivered"
                ? "Units Delivered"
                : "Percentage Delivered";

        if (!isLegacy) {
            if (Object.keys(log).length == 0) {
                buildRollForward(
                    org,
                    salesOrder,
                    salesOrderItem,
                    log,
                    servicePeriods,
                    purchases.incrementalPurchases,
                    endPeriod,
                    rollForward,
                    "Units Delivered",
                    purchases.priorClosedPeriods,
                    jobId,
                    isServiceItemDeleted(serviceItem)
                );
                continue;
            }

            if (logMethod == "unitsDelivered") {
                buildRollForward(
                    org,
                    salesOrder,
                    salesOrderItem,
                    log,
                    servicePeriods,
                    purchases.incrementalPurchases,
                    endPeriod,
                    rollForward,
                    deliveryMethod,
                    purchases.priorClosedPeriods,
                    jobId,
                    isServiceItemDeleted(serviceItem)
                );
            } else {
                buildRollForward(
                    org,
                    salesOrder,
                    salesOrderItem,
                    percentageToUnitsLog,
                    servicePeriods,
                    purchases.incrementalPurchases,
                    endPeriod,
                    rollForward,
                    deliveryMethod,
                    purchases.priorClosedPeriods,
                    jobId,
                    isServiceItemDeleted(serviceItem)
                );
            }
        } else {
            // legacy client
            let beg = 0.0;
            let end = 0.0;
            let consumed = 0.0;

            for (let period of servicePeriods) {
                beg = end;
                consumed = log[period] || 0.0;
                const adds = purchases.incrementalPurchases[period]
                    ? purchases.incrementalPurchases[period]
                    : 0.0;
                const expired = period === endPeriod ? end : 0.0;
                end = beg + adds - expired - consumed;

                rollForward.push({
                    OrgId: org.id,
                    Active_Link_Id: 0,
                    "Customer Id": salesOrder.customerId,
                    "Sales Order Id": salesOrder.id,
                    "Sales Order Item Id":
                        salesOrderItem.rootId || salesOrderItem.id,
                    "Product Id": salesOrderItem.product.id,
                    "Actg Period": period,
                    "Beginning Units": beg,
                    "Consumed Units": consumed,
                    "Overage Units": 0.0,
                    "New Units": adds,
                    "Ending Units": end,
                    "Expired Units": expired,
                    "Beginning Percentage": 0.0,
                    "New Percentage": 0.0,
                    "Consumed Percentage": 0.0,
                    "Overage Percentage": 0.0,
                    "Expired Percentage": 0.0,
                    "Ending Percentage": 0.0,
                    "Cumulative Consumed Percentage": 0.0,
                    "Cumulative Overage Percentage": 0.0,
                    "Log Method": deliveryMethod,
                    "Job Id": jobId,
                    LastModifiedDate: new Date()
                        .toISOString()
                        .slice(0, 19)
                        .replace("T", " ")
                });
            }
        }
    }

    return rollForward;
};

function makeRollForwardRow({
    org,
    salesOrder,
    salesOrderItem,
    actgPeriod,
    beginningUnits,
    newUnits,
    consumedUnits,
    endingUnits,
    overageUnits,
    expiredUnits,
    beginningPercentage,
    newPercentage,
    consumedPercentage,
    overagePercentage,
    expiredPercentage,
    endingPercentage,
    cumulativeConsumedPercentage,
    cumulativeOveragePercentage,
    deliveryMethod,
    jobId
}) {
    return {
        OrgId: org.id,
        Active_Link_Id: 0,
        "Customer Id": salesOrder.customerId,
        "Sales Order Id": salesOrder.id,
        "Sales Order Item Id": salesOrderItem.rootId || salesOrderItem.id,
        "Product Id": salesOrderItem.product.id,
        "Actg Period": actgPeriod,
        "Beginning Units": beginningUnits,
        "New Units": newUnits,
        "Consumed Units": consumedUnits,
        "Ending Units": endingUnits,
        "Overage Units": overageUnits,
        "Expired Units": expiredUnits,
        "Beginning Percentage": beginningPercentage,
        "New Percentage": newPercentage,
        "Consumed Percentage": consumedPercentage,
        "Overage Percentage": overagePercentage,
        "Expired Percentage": expiredPercentage,
        "Ending Percentage": endingPercentage,
        "Cumulative Consumed Percentage": cumulativeConsumedPercentage,
        "Cumulative Overage Percentage": cumulativeOveragePercentage,
        "Log Method": deliveryMethod,
        "Job Id": jobId,
        LastModifiedDate: new Date().toISOString().slice(0, 19).replace("T", " ")
    };
}

function buildRollForward(
    org,
    salesOrder,
    salesOrderItem,
    log,
    servicePeriods,
    purchases,
    endPeriod,
    rollForward,
    deliveryMethod,
    priorClosedPeriods,
    jobId,
    isDeleted
) {
    const itemRollForwardStart = rollForward.length;
    let beg = 0.0;
    let end = 0.0;
    let totalQuantityTillPeriod = 0.0;
    let cumulativeOverageUnits = 0.0;
    let totalComulativeUnitsConsumedUntilLastPeriod = 0.0;

    let totalCumulativeUnitsDelivered = 0.0;
    let totalCumulativeUnitsDeliveredTillLastPeriod = 0.0;
    let priorQuantity = 0.0;
    let priorExpired = 0.0;
    let totalCumulativePriorExpired = 0.0;

    let totalComulativePercentConsumed = 0.0;
    let totalCumulativeOverage = 0.0;

    let index = 0;
    for (let period of servicePeriods) {
        beg = end;

        let consumed = 0.0;
        let deliveredUnits = log[period] || 0.0;
        totalCumulativeUnitsDelivered += deliveredUnits;

        let adds = purchases[period] ? purchases[period] : 0.0;
        totalQuantityTillPeriod += adds;

        let overageUnits = 0.0;
        let expired = 0.0;

        if (index > 0 && adds > 0) {
            // I had somethind that was overage, but now it seems
            // i can consume it. how much is what we are going to determine
            let unitsToConsumeThisPeriod =
                totalCumulativeUnitsDeliveredTillLastPeriod -
                totalComulativeUnitsConsumedUntilLastPeriod;
            unitsToConsumeThisPeriod += deliveredUnits;

            // 1.0 + 0.166 - 0.9) = 0.2666
            const capacityToConsumeThisPeriod =
                totalQuantityTillPeriod -
                totalComulativeUnitsConsumedUntilLastPeriod;

            if (capacityToConsumeThisPeriod >= unitsToConsumeThisPeriod) {
                consumed = unitsToConsumeThisPeriod;
            } else {
                // 0.25 capacityToConsumeThisPeriod
                // 0.3 percToConsumeThisPeriod

                // 0.2 overage + 0.1 currentLog

                consumed = capacityToConsumeThisPeriod;
            }
            // Here we need to fix the overage for reversals while building RF
            overageUnits = deliveredUnits - consumed;
        } else {
            if (totalCumulativeUnitsDelivered <= totalQuantityTillPeriod) {
                // whatever is delivered is consumed
                consumed = deliveredUnits;
            } else {
                // Can I consume something today?
                if (index > 0 && adds < 0) {
                    // quantity has shrinked
                    // units delivered above the reduced quantity move from consumed to overage
                    const unitsAboveQuantity = Math.max(
                        totalCumulativeUnitsDelivered -
                            totalQuantityTillPeriod -
                            cumulativeOverageUnits,
                        0
                    );
                    consumed = deliveredUnits - unitsAboveQuantity;
                    overageUnits = unitsAboveQuantity;
                } else {
                    if (deliveredUnits) {
                        overageUnits =
                            totalCumulativeUnitsDelivered -
                            totalQuantityTillPeriod -
                            cumulativeOverageUnits;
                    }
                    consumed = deliveredUnits - overageUnits;
                }
            }
        }

        cumulativeOverageUnits += overageUnits;
        totalComulativeUnitsConsumedUntilLastPeriod += consumed;
        totalCumulativeUnitsDeliveredTillLastPeriod += deliveredUnits;

        if (priorExpired) {
            end =
                totalQuantityTillPeriod -
                totalComulativeUnitsConsumedUntilLastPeriod;
            expired = beg + adds - end - consumed;
        } else {
            end = beg + adds - Math.abs(expired) - consumed;
        }

        if (period == endPeriod) {
            // we have expired units now
            // consumed this period and everything until last
            end = 0;
            expired = beg + adds - end - consumed;
        }

        if (
            priorClosedPeriods &&
            period == priorClosedPeriods[period] &&
            period != endPeriod
        ) {
            expired = end;
            end = 0;
            priorExpired = expired;
        }

        let newPercentage = adds / totalQuantityTillPeriod;
        const begPec = beg / priorQuantity;
        const addPerc = adds / totalQuantityTillPeriod;

        if (index > 0 && adds && beg) {
            newPercentage =
                addPerc +
                (-1 * begPec +
                    (begPec * priorQuantity) / totalQuantityTillPeriod);
        }

        let consumedPercentage = consumed / totalQuantityTillPeriod;
        let overagePercentage = overageUnits / totalQuantityTillPeriod;
        let expiredPercentage = expired / totalQuantityTillPeriod;
        let endPercentage = end / totalQuantityTillPeriod;

        if (consumedPercentage) {
            totalComulativePercentConsumed += consumedPercentage;
        }

        if (overagePercentage) {
            totalCumulativeOverage += overagePercentage;
        }

        priorQuantity = totalQuantityTillPeriod;

        rollForward.push(
            makeRollForwardRow({
                org,
                salesOrder,
                salesOrderItem,
                actgPeriod: period,
                beginningUnits: beg,
                newUnits: adds,
                consumedUnits: consumed,
                endingUnits: end,
                overageUnits,
                expiredUnits: expired,
                beginningPercentage: begPec,
                newPercentage,
                consumedPercentage,
                overagePercentage,
                expiredPercentage,
                endingPercentage: endPercentage,
                cumulativeConsumedPercentage: deliveredUnits
                    ? totalComulativePercentConsumed
                    : 0.0,
                cumulativeOveragePercentage: deliveredUnits
                    ? totalCumulativeOverage
                    : 0.0,
                deliveryMethod,
                jobId
            })
        );

        index++;
    }

    // For deleted items, freeze at last closed period balance and post reversal in current open period
    if (isDeleted && org.currentPeriod) {
        const itemRows = rollForward.slice(itemRollForwardStart);
        const closedRows = itemRows.filter((row) =>
            Period.isBefore(row["Actg Period"], org.currentPeriod)
        );
        const lastClosedRow = closedRows[closedRows.length - 1];
        const lastEndingBalance = lastClosedRow?.["Ending Units"] || 0;

        rollForward.splice(
            itemRollForwardStart,
            rollForward.length - itemRollForwardStart,
            ...closedRows
        );

        if (lastEndingBalance > 0) {
            const lastEndingPercentage =
                lastClosedRow["Ending Percentage"] || 0;
            rollForward.push(
                makeRollForwardRow({
                    org,
                    salesOrder,
                    salesOrderItem,
                    actgPeriod: org.currentPeriod,
                    beginningUnits: lastEndingBalance,
                    newUnits: -lastEndingBalance,
                    consumedUnits: 0,
                    endingUnits: 0,
                    overageUnits: 0,
                    expiredUnits: 0,
                    beginningPercentage: lastEndingPercentage,
                    newPercentage: -lastEndingPercentage,
                    consumedPercentage: 0,
                    overagePercentage: 0,
                    expiredPercentage: 0,
                    endingPercentage: 0,
                    cumulativeConsumedPercentage:
                        lastClosedRow["Cumulative Consumed Percentage"],
                    cumulativeOveragePercentage:
                        lastClosedRow["Cumulative Overage Percentage"],
                    deliveryMethod,
                    jobId
                })
            );
        }
    }
}

/**
 * Populates DeliveryLogs at RevenueArrangementItem level
 * @param revenueArrangement
 * @param salesOrder
 * @param orgConfig
 * @param errors
 * @returns
 */
export const populateDeliveryLogs = (
    revenueArrangement,
    salesOrder,
    orgConfig,
    errors
) => {
    if (!salesOrder.professionalServiceDelivery) {
        return;
    }
    // Hold all the logs in unconsumedLogs initially and remove if a log can be attached to an raItem
    // And the final array will have the logs which cannot be assigned to any item
    const unconsumedLogs = {};
    // Every group's logs in document order. Groups loaded in order-item mode
    // carry no productId, so they collide on the same unconsumedLogs key.
    const allUnconsumedLogs = [];
    const populateUnconsumedLogs = () => {
        salesOrder.professionalServiceDelivery.soiProfessionalServiceDelivery.forEach(
            (productDeliveryLogs) => {
                let { productId, deliveryLog } = productDeliveryLogs;
                deliveryLog.sort(
                    Comparator.getComparator(
                        ["logDate", "timestamp"],
                        [
                            Comparator.forType("date"),
                            Comparator.forType("number")
                        ]
                    )
                );
                if (
                    revenueArrangement.modificationType ==
                        ModificationType.PROSPECTIVE &&
                    deliveryLog
                ) {
                    deliveryLog.forEach((log) => {
                        // get log's timestamp - * 1000 gives us the milliseconds back
                        const logUpdatedDate = moment(log.timestamp * 1000);
                        const soUpdatedDate = salesOrder.lastUpdateDate;
                        if (
                            log.logDate < revenueArrangement.modificationDate &&
                            (salesOrder.jobId == log.jobId ||
                                Utils.isAfter(logUpdatedDate, soUpdatedDate))
                        ) {
                            // if the logDate is before the modificationDate and the log is received in the currentJob or
                            // in any future job, add a warning that the log cannot be consumed
                            errors.add(Codes.PSD_05, {
                                salesOrderId: salesOrder.id,
                                ...log
                            });
                        }
                    });
                    deliveryLog = deliveryLog.filter(
                        (log) =>
                            log.logDate >= revenueArrangement.modificationDate
                    );
                }
                unconsumedLogs[productId] = deliveryLog.map((log) => {
                    return {
                        effectivePeriod: log.effectivePeriod,
                        logDate: log.logDate,
                        period: log.period,
                        salesOrderItemId: log.salesOrderItemId,
                        serviceEndDate: log.serviceEndDate,
                        unitsDelivered: log.unitsDelivered,
                        serviceDeliveryId: log.serviceDeliveryId,
                        variableQuantity: log.variableQuantity,
                        variableAmount: log.variableAmount,
                        usageType: log.usageType,
                        variableAdjMode: log.variableAdjMode,
                        productCode: log.productCode,
                        linkedProductCode: log.linkedProductCode,
                        linkedProductId: log.linkedProductId,
                        termStartDate: log.termStartDate,
                        termEndDate: log.termEndDate
                    };
                });
                allUnconsumedLogs.push(...unconsumedLogs[productId]);
            }
        );
    };
    const splitLog = (log, units1, units2) => {
        const log1 = Utils.cloneDeep(log);
        log1.unitsDelivered = units1;
        const log2 = Utils.cloneDeep(log);
        log2.unitsDelivered = units2;
        return [log1, log2];
    };
    const isRolloverUnusedUnits = (soItem) => {
        if (soItem.rolloverUnusedUnits == null) {
            return soItem.product.rolloverUnusedUnits;
        }
        return soItem.rolloverUnusedUnits;
    };
    const populateExtendedDeliveryEndDate = (raiByProduct) => {
        Object.keys(raiByProduct).forEach((productCode) => {
            const raItems = raiByProduct[productCode];
            let maxEndDate = "2000-01-01";
            raItems.forEach((raItem) => {
                maxEndDate =
                    raItem.deliveryEndDate > maxEndDate
                        ? raItem.deliveryEndDate
                        : maxEndDate;
            });
            raItems.forEach((raItem) => {
                if (isRolloverUnusedUnits(raItem.salesOrderItem)) {
                    raItem.extendedDeliveryEndDate = maxEndDate;
                }
            });
        });
    };
    const attachLogToRaItem = (log, raItem) => {
        raItem.deliveryLogs.push(log);
        const remainingUnits = raItem.remainingUnits - log.unitsDelivered;
        raItem.remainingUnits = remainingUnits > 0 ? remainingUnits : 0;
    };
    const endDateInclusiveConfig = orgConfig.find(
        (config) => config.id === "properties/ratable_end_date_inclusive"
    );
    const endDateInclusive =
        endDateInclusiveConfig && endDateInclusiveConfig.value;
    const discardLogsBeforeOrderStartDateConfig = orgConfig.find(
        (config) =>
            config.id === "properties/psd/discardLogsBeforeOrderStartDate"
    );
    const discardLogsBeforeOrderStartDateVal =
        discardLogsBeforeOrderStartDateConfig &&
        discardLogsBeforeOrderStartDateConfig.value;
    const discardLogsBeforeOrderStartDate =
        discardLogsBeforeOrderStartDateVal === true ||
        discardLogsBeforeOrderStartDateVal === "true";

    const compareLogDateWithRaItem = (log, raItem) => {
        const {
            deliveryStartDate: startDate,
            deliveryEndDate,
            extendedDeliveryEndDate
        } = raItem;
        const beforeEndDate = endDateInclusive
            ? Utils.isSameOrBefore
            : Utils.isBefore;
        const startPeriod = Period.toPeriod(startDate);
        const logPeriod =
            log.effectivePeriod || log.period || Period.toPeriod(log.logDate);
        const isAfterStartPeriod = Period.isSameOrAfter(logPeriod, startPeriod);
        const isBeforeEndDate = beforeEndDate(
            log.logDate,
            extendedDeliveryEndDate || deliveryEndDate
        );
        return [isAfterStartPeriod, isBeforeEndDate, startPeriod];
    };

    let discardedLogs = {};

    const addToDiscardedLogs = (log, productCode) => {
        if (!discardedLogs[productCode]) {
            discardedLogs[productCode] = [];
        }
        discardedLogs[productCode].push(log);
    };

    const isCreditNote = (soi) =>
        soi.invoiceNumber &&
        soi.referenceInvoiceNumber &&
        soi.invoiceNumber !== soi.referenceInvoiceNumber;

    // Populate unconsumedLogs at revenueArrangement [productCode -> deliveryLogs]
    populateUnconsumedLogs();

    const activeRaitems = revenueArrangement.revenueArrangementItem.filter(
        (raItem) =>
            !raItem.salesOrderItem.isDeleted &&
            !ItemType.isFrozen(raItem.itemType) &&
            !isCreditNote(raItem.salesOrderItem)
    );
    const raiByProduct = Utils.groupByOnArray(
        activeRaitems,
        (item) => item.salesOrderItem.product.code
    );

    // Populate maximum service end date to all revenueArrangementItems if rolloverUnusedUnits is configured
    populateExtendedDeliveryEndDate(raiByProduct);

    // Clear deliveryLogs mapped to the existing revenueArrangementItems
    revenueArrangement.revenueArrangementItem.forEach((raItem) => {
        raItem.deliveryLogs = [];
        raItem.remainingUnits = raItem.salesOrderItem.quantity;
    });

    // REV-17972 — match-fields mode. When configured, each log resolves to
    // exactly one line by the configured fields and date rules. No FIFO
    // pooling, no base-product clone, no rollover stretch, no TRAN_45.
    const matchFields = matchFieldsFromOrgConfig(orgConfig);
    if (matchFields) {
        for (const log of allUnconsumedLogs) {
            const result = matchLogToLine(
                log,
                activeRaitems,
                matchFields,
                endDateInclusive
            );
            if (result.raItem) {
                attachLogToRaItem(log, result.raItem);
            } else if (errors) {
                errors.add(result.code, {
                    salesOrderId: salesOrder.id,
                    candidates: result.candidates,
                    ...log
                });
            }
        }
        return;
    }

    // "- Add a new flag to Product PO, Track Base Product Usage (trackBaseProductUsage)
    for (let soi of salesOrder.salesOrderItem) {
        let product = soi.product;
        if (product.entityType == "PO" && product.trackBaseProductUsage) {
            const baseDeliveryLogs = cloneDeep(unconsumedLogs[product.parent]);
            unconsumedLogs[product.code] = baseDeliveryLogs;
        }
    }

    Object.keys(raiByProduct).forEach((productCode) => {
        const raItems = raiByProduct[productCode];
        const deliveryLogs = unconsumedLogs[productCode];
        raItems.sort(
            Comparator.getComparator(
                [(raItem) => raItem.deliveryStartDate],
                [Comparator.forType("date")]
            )
        );
        if (deliveryLogs && deliveryLogs.length > 0) {
            for (const raItem of raItems) {
                // Process logs until the current revenueArrangementItem has units available to consume logs
                for (
                    let i = 0;
                    i < deliveryLogs.length && raItem.remainingUnits > 0;

                ) {
                    let { remainingUnits } = raItem;
                    const log = deliveryLogs[i];
                    // When the log is at salesOrderItemId, attach the log to the specific revenueArrangementItem
                    const soiRaItems = log.salesOrderItemId
                        ? raItems.filter(
                              (raItem) =>
                                  raItem.salesOrderItemId ==
                                  log.salesOrderItemId
                          )
                        : [];
                    if (soiRaItems.length > 0) {
                        const soiRaItem = soiRaItems[0];
                        deliveryLogs.splice(i, 1);
                        attachLogToRaItem(log, soiRaItem);
                    } else {
                        // If the logDate does not occur with in the RAI delivery dates,
                        //    the log cannot be consumed by the current revenueArrangementItem
                        const [
                            isAfterStartPeriod,
                            isBeforeEndDate,
                            startPeriod
                        ] = compareLogDateWithRaItem(log, raItem);
                        if (!isBeforeEndDate) {
                            break;
                        }
                        deliveryLogs.splice(i, 1);
                        if (!isAfterStartPeriod) {
                            if (discardLogsBeforeOrderStartDate) {
                                addToDiscardedLogs(log, productCode);
                                continue;
                            }
                            // A delivery cannot recognize before its arrangement item
                            // is active, so it books in the first active period.
                            log.effectivePeriod = startPeriod;
                        }
                        if (log.unitsDelivered <= remainingUnits) {
                            attachLogToRaItem(log, raItem);
                        } else {
                            // log cannot be consumed completely, will split the logs
                            const [log1, log2] = splitLog(
                                log,
                                remainingUnits,
                                log.unitsDelivered - remainingUnits
                            );
                            attachLogToRaItem(log1, raItem);
                            // rolloverUnusedUnits is enabled, add the log to the start of the list to be consumed by other items
                            if (isRolloverUnusedUnits(raItem.salesOrderItem)) {
                                deliveryLogs.unshift(log2);
                            } else {
                                addToDiscardedLogs(log2, productCode);
                            }
                        }
                    }
                }
            }
        }
    });

    Object.keys(discardedLogs).forEach((productCode) => {
        unconsumedLogs[productCode].unshift(...discardedLogs[productCode]);
    });

    discardedLogs = {};
    // Attach overage units to the raItem with the least startDate
    // and populate errors for all logs which cannot be assigned to any item
    Object.keys(raiByProduct).forEach((productCode) => {
        const raItems = raiByProduct[productCode];
        const deliveryLogs = unconsumedLogs[productCode];
        if (deliveryLogs && deliveryLogs.length > 0) {
            for (const raItem of raItems) {
                for (let i = 0; i < deliveryLogs.length; ) {
                    const log = deliveryLogs[i];
                    const [isAfterStartPeriod, isBeforeEndDate, startPeriod] =
                        compareLogDateWithRaItem(log, raItem);
                    if (!isBeforeEndDate) {
                        break;
                    }
                    deliveryLogs.splice(i, 1);
                    if (!isAfterStartPeriod) {
                        if (discardLogsBeforeOrderStartDate) {
                            addToDiscardedLogs(log, productCode);
                            continue;
                        }
                        log.effectivePeriod = startPeriod;
                    }
                    raItem.deliveryLogs.push(log);
                }
            }
        }
    });
    if (errors) {
        Object.keys(discardedLogs).forEach((productCode) => {
            unconsumedLogs[productCode].push(...discardedLogs[productCode]);
        });
        Object.keys(unconsumedLogs).forEach((productCode) => {
            unconsumedLogs[productCode].forEach((log) => {
                errors.add(Codes.TRAN_45, {
                    salesOrderId: salesOrder.id,
                    ...log
                });
            });
        });
    }
};
